/**
 * The super-admin Signups page's data (docs/plg-review.md → Admin API).
 * Shapes mirror the contract exactly; dates arrive as ISO strings.
 */

export type SignupView = 'review' | 'all' | 'visitors' | 'stuck_email' | 'stuck_snippet' | 'approved' | 'blocked' | 'onboarded';
export type SignupRowStatus = 'visitor' | 'pending_embed' | 'pending_review' | 'active' | 'blocked';
export type ReviewStatus = 'pending' | 'approved' | 'blocked';
export type Verdict = 'business' | 'unclear' | 'not_business';
export type BlockReason = 'no_business' | 'unrelated_business' | 'duplicate' | 'fake_details' | 'other';
export type DemoStatus = 'new' | 'contacted' | 'scheduled' | 'done' | 'lost';

export const BLOCK_REASONS: BlockReason[] = ['no_business', 'unrelated_business', 'duplicate', 'fake_details', 'other'];
export const DEMO_STATUSES: DemoStatus[] = ['new', 'contacted', 'scheduled', 'done', 'lost'];

/** The seven milestones, in order: each an ISO date, or null when not reached. */
export const SIGNUP_STEPS = ['website', 'account', 'emailVerified', 'sdkConnected', 'reviewRequested', 'decided', 'onboarded'] as const;
export type SignupStep = (typeof SIGNUP_STEPS)[number];

export interface SignupRow {
  /** vendorId, or `j:<journeyId>` for a visitor. */
  id: string;
  kind: 'account' | 'visitor';
  vendorId?: string;
  domain?: string;
  title?: string;
  platform?: string | null;
  email?: string;
  name?: string;
  locale?: string;
  createdAt: string;
  lastActivityAt: string;
  steps: Partial<Record<SignupStep, string | null>>;
  status: SignupRowStatus;
  review?: { status: ReviewStatus; reason?: string; decidedByName?: string } | null;
  verdict?: { verdict: Verdict; score: number; ai?: string | null } | null;
  riskFlags?: string[];
  demoRequested?: boolean;
  /** Visitors: how many times a website was checked. */
  websiteChecks?: number;
  /** Accounts: is the SDK still on the site (`steps.sdkConnected` only says it was seen once). */
  sdkPresence?: SdkPresence | null;
}

/**
 * live: seen in the last days (or a check just found it) · quiet: not seen
 * lately, not checked since · removed: a check after the last sighting did not
 * find it · never: never seen.
 */
export type SdkPresenceStatus = 'live' | 'quiet' | 'removed' | 'never';
/** Why a live check could not judge the site. */
export type SdkCheckError = 'no_domain' | 'unreachable' | 'blocked' | 'not_found' | 'http_error';

export interface SdkPresence {
  status: SdkPresenceStatus;
  firstSeenAt: string | null;
  lastSeenAt: string | null;
  domain: string | null;
  lastCheck?: { at: string; found: boolean; url?: string; error?: SdkCheckError | string };
}

export interface SignupListResponse {
  rows: SignupRow[];
  total: number;
  page: number;
  limit: number;
}

export interface SignupStats {
  funnel: { websiteChecked: number; accounts: number; emailVerified: number; connected: number; approved: number; onboarded: number };
  pendingReview: number;
  blocked: number;
  demoRequestsNew: number;
  medianReviewMinutes: number | null;
}

export const EVIDENCE_SIGNALS = ['productSchema', 'prices', 'cart', 'contact', 'legal', 'social', 'emailMatchesDomain', 'snippetOnHomepage'] as const;
export type EvidenceSignal = (typeof EVIDENCE_SIGNALS)[number];

export interface WebsiteEvidence {
  checkedAt: string;
  url: string;
  httpStatus?: number;
  title?: string;
  description?: string;
  lang?: string;
  platform?: string | null;
  screenshotUrl?: string | null;
  domain: { registeredAt?: string | null; ageDays?: number | null; registrar?: string | null };
  pages: { sitemapUrls?: number | null; productUrls?: number | null; internalLinks: number; words: number; images: number };
  signals: Record<EvidenceSignal, boolean>;
  sdkSeenFrom?: { origin?: string; page?: string; at?: string };
  score: number;
  verdict: Verdict;
  ai?: { verdict: Verdict; sells?: string; fit: 'core' | 'adjacent' | 'unrelated'; summary: string; model?: string } | null;
  error?: string;
  /** The homepage could not be read (down, or behind a bot wall); the verdict stays unclear. */
  unreadable?: boolean;
}

export interface SignupEvent {
  type: string;
  at: string;
  data?: Record<string, any>;
}

export interface DemoRequest {
  _id?: string;
  id?: string;
  name: string;
  email: string;
  phone?: string;
  company?: string;
  website?: string;
  sells: 'online' | 'offline' | 'both';
  reason: 'offline_store' | 'cant_install_now' | 'want_walkthrough' | 'other';
  message?: string;
  /** Free text, from requests made before the form had a date and time. */
  preferredTime?: string;
  /** The call time the requester picked. */
  preferredAt?: string | null;
  /** Their IANA time zone. */
  timeZone?: string | null;
  locale?: string;
  /** The linked vendor's id, when the request came from a signed-in vendor. */
  vendor?: string | null;
  journeyId?: string;
  /** Booked from this prospect demo (<slug>.ardealy.link), and the prospect it was made for. */
  demoSlug?: string | null;
  prospectId?: string | null;
  prospectName?: string | null;
  status: DemoStatus;
  note?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface DemoRequestListResponse {
  rows: DemoRequest[];
  total: number;
  page: number;
  limit: number;
}

export interface SignupUser {
  id: string;
  email: string;
  name?: string;
  isVerified: boolean;
  lastLoginAt?: string | null;
}

export interface SignupSelfServe {
  status?: string;
  createdAt?: string;
  emailVerifiedAt?: string | null;
  sdkConnectedAt?: string | null;
  activatedAt?: string | null;
  reviewRequestedAt?: string | null;
  signupIp?: string;
  riskFlags?: string[];
  review?: {
    status: ReviewStatus;
    decidedAt?: string;
    decidedBy?: string;
    decidedByName?: string;
    reason?: BlockReason;
    message?: string;
    note?: string;
  };
  evidence?: WebsiteEvidence;
  journeyId?: string;
  /** The last live look for the snippet ("Check now", or the daily sweep). */
  sdkCheck?: { at: string; found: boolean; url?: string; error?: string } | null;
}

export interface SignupDetail extends SignupRow {
  users: SignupUser[];
  selfServe?: SignupSelfServe;
  /** Newest first, at most 200. */
  events: SignupEvent[];
  demoRequests: DemoRequest[];
  /** The plan's slug (the contract says "plan slug"; either name is read). */
  plan?: string | null;
  planSlug?: string | null;
  onboardedAt?: string | null;
}

export interface BlockSignupPayload {
  reason: BlockReason;
  message?: string;
  note?: string;
  notify: boolean;
}

/** A demo request's id, whichever field the API uses. */
export const demoId = (d: DemoRequest) => String(d._id ?? d.id ?? '');
